import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Edit3, Download, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminClient, apiClient, clearAdminToken, formatApiError, API, getAdminToken } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";

const inputCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
const labelCls = "font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]";
const btnCls = "rounded-full bg-[#D4AF37] px-6 py-3 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A1E3C] transition-colors hover:bg-[#C9A227] disabled:opacity-60";

const TABS = ["Samples", "Blog", "Videos", "Reviews", "Rates", "Promos", "Social", "Settings", "Leads"];

const SamplesTab = () => {
  const [samples, setSamples] = useState([]);
  const [form, setForm] = useState({ title: "", subject: "", level: "Undergraduate", pages: 8 });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => apiClient.get("/samples").then((r) => setSamples(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach a PDF/DOC file.");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await adminClient.post("/admin/samples", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sample published.");
      setForm({ title: "", subject: "", level: "Undergraduate", pages: 8 });
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    await adminClient.delete(`/admin/samples/${id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Sample removed.");
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="admin-samples-tab">
      <form onSubmit={submit} className="luxury-card space-y-4 rounded-xl p-6" data-testid="admin-sample-form">
        <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Upload New Sample</h3>
        <div className="space-y-2"><Label className={labelCls}>Title</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-sample-title" className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label className={labelCls}>Subject</Label>
            <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="admin-sample-subject" className={inputCls} /></div>
          <div className="space-y-2"><Label className={labelCls}>Level</Label>
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger data-testid="admin-sample-level" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]">
                {["High School", "Undergraduate", "Masters", "PhD"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        <div className="space-y-2"><Label className={labelCls}>Pages</Label>
          <Input type="number" min={1} value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} data-testid="admin-sample-pages" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>File (PDF/DOC/DOCX, max 15MB)</Label>
          <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-sample-file" className={inputCls} /></div>
        <button type="submit" disabled={loading} data-testid="admin-sample-submit" className={btnCls}>
          {loading ? <Loader2 size={14} className="inline animate-spin" /> : <Plus size={14} className="inline" />} Publish Sample
        </button>
      </form>
      <div className="space-y-3" data-testid="admin-samples-list">
        {samples.length === 0 && <p className="text-sm text-[#5E6E85]">No samples yet.</p>}
        {samples.map((s, i) => (
          <div key={s.id} className="luxury-card flex items-center justify-between gap-4 rounded-xl p-4" data-testid={`admin-sample-row-${i}`}>
            <div className="flex items-center gap-3">
              <FileText size={18} className="shrink-0 text-[#A07E1F]" />
              <div>
                <p className="text-sm font-medium text-[#12294D]">{s.title}</p>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#75849B]">{s.subject} · {s.level} · {s.pages}p</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a href={`${API}/samples/${s.id}/download`} data-testid={`admin-sample-download-${i}`} className="rounded-full border border-[#D4AF37]/40 p-2 text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10"><Download size={14} /></a>
              <button onClick={() => remove(s.id)} data-testid={`admin-sample-delete-${i}`} className="rounded-full border border-red-500/40 p-2 text-red-400 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", tags: "", content: "" });
  const [loading, setLoading] = useState(false);

  const load = () => apiClient.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) await adminClient.put(`/admin/blog/${editing}`, payload);
      else await adminClient.post("/admin/blog", payload);
      toast.success(editing ? "Article updated." : "Article published.");
      setForm({ title: "", excerpt: "", tags: "", content: "" });
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, excerpt: p.excerpt, tags: (p.tags || []).join(", "), content: p.content });
  };

  const remove = async (id) => {
    await adminClient.delete(`/admin/blog/${id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Article deleted.");
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="admin-blog-tab">
      <form onSubmit={submit} className="luxury-card space-y-4 rounded-xl p-6" data-testid="admin-blog-form">
        <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">{editing ? "Edit Article" : "Write New Article"}</h3>
        <div className="space-y-2"><Label className={labelCls}>Title</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-blog-title" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Excerpt (SEO description)</Label>
          <Textarea rows={2} required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="admin-blog-excerpt" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Tags (comma separated)</Label>
          <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="admin-blog-tags" placeholder="Dissertation Guidance, UK Universities" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Content (blank line = new paragraph)</Label>
          <Textarea rows={10} required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="admin-blog-content" className={inputCls} /></div>
        <div className="flex gap-3">
          <button type="submit" disabled={loading} data-testid="admin-blog-submit" className={btnCls}>
            {loading ? <Loader2 size={14} className="inline animate-spin" /> : <Plus size={14} className="inline" />} {editing ? "Save Changes" : "Publish Article"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm({ title: "", excerpt: "", tags: "", content: "" }); }} data-testid="admin-blog-cancel" className="rounded-full border border-[#D4AF37]/40 px-6 py-3 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F]">
              Cancel
            </button>
          )}
        </div>
      </form>
      <div className="space-y-3" data-testid="admin-blog-list">
        {posts.length === 0 && <p className="text-sm text-[#5E6E85]">No articles yet.</p>}
        {posts.map((p, i) => (
          <div key={p.id} className="luxury-card flex items-center justify-between gap-4 rounded-xl p-4" data-testid={`admin-blog-row-${i}`}>
            <div>
              <p className="text-sm font-medium text-[#12294D]">{p.title}</p>
              <p className="font-mono-accent text-[10px] tracking-[0.1em] text-[#75849B]">/blog/{p.slug}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => edit(p)} data-testid={`admin-blog-edit-${i}`} className="rounded-full border border-[#D4AF37]/40 p-2 text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10"><Edit3 size={14} /></button>
              <button onClick={() => remove(p.id)} data-testid={`admin-blog-delete-${i}`} className="rounded-full border border-red-500/40 p-2 text-red-400 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", meta: "", quote: "", rating: 5 });
  const [image, setImage] = useState(null);

  const load = () => apiClient.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (image) fd.append("image", image);
      await adminClient.post("/admin/reviews", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Review added.");
      setForm({ name: "", meta: "", quote: "", rating: 5 });
      setImage(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    await adminClient.delete(`/admin/reviews/${id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Review removed.");
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="admin-reviews-tab">
      <form onSubmit={submit} className="luxury-card space-y-4 rounded-xl p-6" data-testid="admin-review-form">
        <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Add Review</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label className={labelCls}>Student Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-review-name" placeholder="Hannah M." className={inputCls} /></div>
          <div className="space-y-2"><Label className={labelCls}>Rating</Label>
            <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
              <SelectTrigger data-testid="admin-review-rating" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]">
                {[5, 4, 3].map((r) => <SelectItem key={r} value={String(r)}>{r} stars</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        <div className="space-y-2"><Label className={labelCls}>Course & Location</Label>
          <Input required value={form.meta} onChange={(e) => setForm({ ...form, meta: e.target.value })} data-testid="admin-review-meta" placeholder="MSc Management — London, UK" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Review Text</Label>
          <Textarea rows={4} required value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="admin-review-quote" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Student Photo (optional — JPG/PNG, max 5MB)</Label>
          <Input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => setImage(e.target.files?.[0] || null)} data-testid="admin-review-image" className={inputCls} /></div>
        <button type="submit" data-testid="admin-review-submit" className={btnCls}><Plus size={14} className="inline" /> Add Review</button>
      </form>
      <div className="space-y-3" data-testid="admin-reviews-list">
        {reviews.map((r, i) => (
          <div key={r.id} className="luxury-card flex items-start justify-between gap-4 rounded-xl p-4" data-testid={`admin-review-row-${i}`}>
            <div>
              <p className="text-sm font-medium text-[#12294D]">{r.name} · {"★".repeat(r.rating)}</p>
              <p className="text-xs text-[#75849B]">{r.meta}</p>
              <p className="mt-2 line-clamp-2 text-sm italic text-[#46586F]">"{r.quote}"</p>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`admin-review-delete-${i}`} className="shrink-0 rounded-full border border-red-500/40 p-2 text-red-400 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const RatesTab = () => {
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiClient.get("/rates").then((r) => setRates(r.data)).catch(() => {}); }, []);
  if (!rates) return <p className="text-[#5E6E85]">Loading rates...</p>;

  const setLevel = (i, v) => {
    const levels = [...rates.levels];
    levels[i] = { ...levels[i], rate250: parseFloat(v) || 0 };
    setRates({ ...rates, levels });
  };
  const setMult = (i, v) => {
    const deadlines = [...rates.deadlines];
    deadlines[i] = { ...deadlines[i], multiplier: parseFloat(v) || 1 };
    setRates({ ...rates, deadlines });
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminClient.put("/admin/rates", rates);
      toast.success("Rates updated — calculator is now live with new pricing.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-rates-tab">
      <div className="luxury-card rounded-xl p-6">
        <h3 className="mb-5 font-serif-display text-xl font-semibold text-[#12294D]">Rate per 250 words ({rates.currency})</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {rates.levels.map((l, i) => (
            <div key={l.value} className="space-y-2">
              <Label className={labelCls}>{l.label}</Label>
              <Input type="number" step="0.5" min="0" value={l.rate250} onChange={(e) => setLevel(i, e.target.value)} data-testid={`admin-rate-${l.value}`} className={inputCls} />
            </div>
          ))}
        </div>
      </div>
      <div className="luxury-card rounded-xl p-6">
        <h3 className="mb-5 font-serif-display text-xl font-semibold text-[#12294D]">Deadline Multipliers</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {rates.deadlines.map((d, i) => (
            <div key={d.value} className="space-y-2">
              <Label className={labelCls}>{d.label}</Label>
              <Input type="number" step="0.05" min="0.5" value={d.multiplier} onChange={(e) => setMult(i, e.target.value)} data-testid={`admin-multiplier-${d.value}`} className={inputCls} />
            </div>
          ))}
        </div>
      </div>
      <div className="luxury-card rounded-xl p-6">
        <div className="max-w-xs space-y-2">
          <Label className={labelCls}>Minimum Order ({rates.currency})</Label>
          <Input type="number" step="1" min="0" value={rates.min_order} onChange={(e) => setRates({ ...rates, min_order: parseFloat(e.target.value) || 0 })} data-testid="admin-min-order" className={inputCls} />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-rates-save" className={btnCls}>
        {saving ? <Loader2 size={14} className="inline animate-spin" /> : null} Save Rates
      </button>
    </div>
  );
};

const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiClient.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings) return <p className="text-[#5E6E85]">Loading settings...</p>;

  const save = async () => {
    setSaving(true);
    try {
      await adminClient.put("/admin/settings", settings);
      toast.success("Contact details updated across the site.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="luxury-card max-w-xl space-y-5 rounded-xl p-6" data-testid="admin-settings-tab">
      <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Site Contact Details</h3>
      <div className="space-y-2"><Label className={labelCls}>Support Email</Label>
        <Input type="email" value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} data-testid="admin-settings-email" className={inputCls} /></div>
      <div className="space-y-2"><Label className={labelCls}>Phone</Label>
        <Input value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="admin-settings-phone" className={inputCls} /></div>
      <div className="space-y-2"><Label className={labelCls}>WhatsApp (with country code)</Label>
        <Input value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} data-testid="admin-settings-whatsapp" className={inputCls} /></div>
      <button onClick={save} disabled={saving} data-testid="admin-settings-save" className={btnCls}>
        {saving ? <Loader2 size={14} className="inline animate-spin" /> : null} Save Settings
      </button>
    </div>
  );
};

const LeadsTab = () => {
  const [data, setData] = useState({ enquiries: [], orders: [], briefs: [] });
  const [view, setView] = useState("enquiries");

  useEffect(() => {
    Promise.all([
      adminClient.get("/admin/enquiries").catch(() => ({ data: [] })),
      adminClient.get("/admin/orders").catch(() => ({ data: [] })),
      adminClient.get("/admin/briefs").catch(() => ({ data: [] })),
    ]).then(([e, o, b]) => setData({ enquiries: e.data, orders: o.data, briefs: b.data }));
  }, []);

  const downloadBrief = async (b) => {
    try {
      const res = await adminClient.get(`/admin/briefs/${b.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.file_name || "brief.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("File unavailable."); }
  };

  const downloadAttachment = async (orderId, idx, name) => {
    try {
      const res = await adminClient.get(`/admin/orders/${orderId}/attachments/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || `attachment-${idx + 1}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("File unavailable."); }
  };

  const downloadExcel = async () => {
    try {
      const res = await adminClient.get(`/admin/export/${view}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myhomeworkassist-${view}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${view} spreadsheet downloaded.`);
    } catch { toast.error("Export failed. Try again."); }
  };

  const rows = data[view];

  return (
    <div data-testid="admin-leads-tab">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {["enquiries", "orders", "briefs"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`leads-view-${v}`}
            className={`rounded-full px-5 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${view === v ? "bg-[#D4AF37] text-[#0A1E3C]" : "border border-[#D4AF37]/30 text-[#A07E1F]"}`}>
            {v} ({data[v].length})
          </button>
        ))}
        <button onClick={downloadExcel} data-testid="leads-download-excel"
          className="flex items-center gap-2 rounded-full border border-[#A07E1F]/50 px-5 py-2 font-mono-accent text-[11px] font-bold uppercase tracking-[0.15em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/15">
          <FileText size={13} /> Download {view} (.xlsx)
        </button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-[#5E6E85]" data-testid="leads-empty">No {view} yet.</p>}
        {rows.map((r, i) => (
          <div key={r.id} className="luxury-card rounded-xl p-5" data-testid={`lead-row-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#12294D]">{r.name} · <span className="text-[#5E6E85]">{r.email}</span></p>
                <p className="mt-1 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#A07E1F]">
                  {view === "enquiries" && `${r.university} · ${r.course} · ${r.assignment_type} · ${r.word_count} words · ${r.deadline}`}
                  {view === "orders" && `${r.service_type} · ${r.academic_level} · ${r.word_count} words · ${r.deadline} · ${r.amount_gbp != null ? `£${r.amount_gbp}` : "no amount"} · ${r.payment_status || r.status}${r.promo_code ? ` · ${r.promo_code}` : ""}`}
                  {view === "briefs" && `${r.university} · ${r.course} · ${r.support_type} · ${r.word_count} words · ${r.deadline}`}
                </p>
                {(r.message || r.details) && <p className="mt-2 text-sm text-[#46586F]">{r.message || r.details}</p>}
                {view === "orders" && r.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`lead-order-attachments-${i}`}>
                    {r.attachments.map((att, j) => (
                      <button key={j} onClick={() => downloadAttachment(r.id, j, att.file_name)} data-testid={`lead-order-attachment-${i}-${j}`} className="flex items-center gap-2 rounded-full border border-[#A07E1F]/40 px-3.5 py-1.5 font-mono-accent text-[10px] tracking-[0.08em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10">
                        <Download size={12} /> {att.file_name}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-[#75849B]">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              {view === "briefs" && r.file_name && (
                <button onClick={() => downloadBrief(r)} data-testid={`lead-brief-download-${i}`} className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10">
                  <Download size={13} /> {r.file_name}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VideosTab = () => {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", video_url: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => apiClient.get("/video-blogs").then((r) => setVideos(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !form.video_url.trim()) return toast.error("Add a YouTube link or upload a video file.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      if (form.video_url.trim()) fd.append("video_url", form.video_url.trim());
      if (file) fd.append("file", file);
      await adminClient.post("/admin/video-blogs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video blog published.");
      setForm({ title: "", description: "", video_url: "" });
      setFile(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const remove = async (id) => {
    await adminClient.delete(`/admin/video-blogs/${id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Video removed.");
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="admin-videos-tab">
      <form onSubmit={submit} className="luxury-card space-y-4 rounded-xl p-6" data-testid="admin-video-form">
        <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Add Video Blog</h3>
        <div className="space-y-2"><Label className={labelCls}>Heading / Title</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-video-title" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Description</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-video-description" className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>YouTube / Video Link (optional)</Label>
          <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="admin-video-url" placeholder="https://youtube.com/watch?v=..." className={inputCls} /></div>
        <div className="space-y-2"><Label className={labelCls}>Or Upload Video (MP4/WEBM/MOV, max 150MB)</Label>
          <Input type="file" accept=".mp4,.webm,.mov" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-video-file" className={inputCls} /></div>
        <button type="submit" disabled={loading} data-testid="admin-video-submit" className={btnCls}>
          {loading ? <Loader2 size={14} className="inline animate-spin" /> : <Plus size={14} className="inline" />} Publish Video Blog
        </button>
      </form>
      <div className="space-y-3" data-testid="admin-videos-list">
        {videos.length === 0 && <p className="text-sm text-[#5E6E85]">No video blogs yet.</p>}
        {videos.map((v, i) => (
          <div key={v.id} className="luxury-card flex items-center justify-between gap-4 rounded-xl p-4" data-testid={`admin-video-row-${i}`}>
            <div>
              <p className="text-sm font-medium text-[#12294D]">{v.title}</p>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#75849B]">
                {v.has_file ? `Uploaded: ${v.file_name || "video"}` : "Linked video"}
              </p>
            </div>
            <button onClick={() => remove(v.id)} data-testid={`admin-video-delete-${i}`} className="shrink-0 rounded-full border border-red-500/40 p-2 text-red-500 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SOCIAL_FIELDS = [
  { key: "facebook", label: "Facebook URL" },
  { key: "instagram", label: "Instagram URL" },
  { key: "x", label: "X (Twitter) URL" },
  { key: "youtube", label: "YouTube URL" },
  { key: "trustpilot", label: "Trustpilot URL" },
];

const SocialTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiClient.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings) return <p className="text-[#5E6E85]">Loading social links...</p>;

  const socials = settings.socials || {};

  const save = async () => {
    setSaving(true);
    try {
      await adminClient.put("/admin/settings", {
        email: settings.email, phone: settings.phone, whatsapp: settings.whatsapp, socials,
      });
      toast.success("Social links updated across the site.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="luxury-card max-w-xl space-y-5 rounded-xl p-6" data-testid="admin-social-tab">
      <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Social Media Links</h3>
      <p className="text-xs text-[#5E6E85]">These appear in the top bar, footer and mobile menu across the website.</p>
      {SOCIAL_FIELDS.map((f) => (
        <div key={f.key} className="space-y-2">
          <Label className={labelCls}>{f.label}</Label>
          <Input
            value={socials[f.key] || ""}
            onChange={(e) => setSettings({ ...settings, socials: { ...socials, [f.key]: e.target.value } })}
            data-testid={`admin-social-${f.key}`}
            placeholder={`https://${f.key}.com/myhomeworkassist`}
            className={inputCls}
          />
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="admin-social-save" className={btnCls}>
        {saving ? <Loader2 size={14} className="inline animate-spin" /> : null} Save Social Links
      </button>
    </div>
  );
};

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", percent: 15 });
  const [loading, setLoading] = useState(false);

  const load = () => adminClient.get("/admin/promos").then((r) => setPromos(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminClient.post("/admin/promos", form);
      toast.success(`Code ${data.code} created — customers save ${form.percent}%.`);
      setForm({ code: "", percent: 15 });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const toggle = async (id) => {
    await adminClient.put(`/admin/promos/${id}/toggle`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const remove = async (id) => {
    await adminClient.delete(`/admin/promos/${id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Code deleted.");
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="admin-promos-tab">
      <form onSubmit={submit} className="luxury-card space-y-4 rounded-xl p-6" data-testid="admin-promo-form">
        <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">Create Discount Code</h3>
        <p className="text-xs text-[#5E6E85]">Customers enter this code on the order page to get an instant discount.</p>
        <div className="space-y-2"><Label className={labelCls}>Code (letters & numbers)</Label>
          <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="admin-promo-code" placeholder="e.g. STUDENT20" className={`${inputCls} font-mono-accent uppercase tracking-[0.1em]`} /></div>
        <div className="space-y-2"><Label className={labelCls}>Discount %</Label>
          <Input type="number" min={1} max={90} required value={form.percent} onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })} data-testid="admin-promo-percent" className={inputCls} /></div>
        <button type="submit" disabled={loading} data-testid="admin-promo-submit" className={btnCls}>
          {loading ? <Loader2 size={14} className="inline animate-spin" /> : <Plus size={14} className="inline" />} Create Code
        </button>
      </form>
      <div className="space-y-3" data-testid="admin-promos-list">
        {promos.length === 0 && <p className="text-sm text-[#5E6E85]">No discount codes yet.</p>}
        {promos.map((p, i) => (
          <div key={p.id} className="luxury-card flex items-center justify-between gap-4 rounded-xl p-4" data-testid={`admin-promo-row-${i}`}>
            <div>
              <p className="font-mono-accent text-sm font-bold tracking-[0.1em] text-[#12294D]">{p.code}</p>
              <p className="text-xs text-[#5E6E85]">{p.percent}% off · {p.active ? "Active" : "Disabled"}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(p.id)} data-testid={`admin-promo-toggle-${i}`} className={`rounded-full border px-4 py-1.5 font-mono-accent text-[10px] uppercase tracking-[0.12em] transition-colors ${p.active ? "border-[#A07E1F]/40 text-[#A07E1F] hover:bg-[#D4AF37]/10" : "border-[#75849B]/40 text-[#75849B] hover:bg-[#75849B]/10"}`}>
                {p.active ? "Disable" : "Enable"}
              </button>
              <button onClick={() => remove(p.id)} data-testid={`admin-promo-delete-${i}`} className="rounded-full border border-red-500/40 p-2 text-red-500 transition-colors hover:bg-red-500/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const [tab, setTab] = useState("Samples");
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) return navigate("/admin");
    adminClient.get("/admin/me")
      .then(() => setChecking(false))
      .catch(() => { clearAdminToken(); navigate("/admin"); });
  }, [navigate]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin");
  };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="animate-spin text-[#A07E1F]" /></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="border-b border-[#D4AF37]/15">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-logo-link"><Logo /></Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#5E6E85] transition-colors hover:text-[#A07E1F]">View Site</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
        <h1 className="font-serif-display text-3xl font-semibold text-[#12294D] sm:text-4xl" data-testid="admin-dashboard-heading">
          Content <span className="italic text-[#A07E1F]">Portal.</span>
        </h1>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`rounded-full px-6 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === t ? "bg-[#D4AF37] text-[#0A1E3C]" : "border border-[#D4AF37]/30 text-[#A07E1F] hover:bg-[#D4AF37]/10"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="mt-10">
          {tab === "Samples" && <SamplesTab />}
          {tab === "Blog" && <BlogTab />}
          {tab === "Videos" && <VideosTab />}
          {tab === "Reviews" && <ReviewsTab />}
          {tab === "Rates" && <RatesTab />}
          {tab === "Promos" && <PromosTab />}
          {tab === "Social" && <SocialTab />}
          {tab === "Settings" && <SettingsTab />}
          {tab === "Leads" && <LeadsTab />}
        </div>
      </main>
    </div>
  );
};
